import React, { useState, useEffect } from 'react';
import { 
  Search, X, Check, Building2, Users, Filter, 
  ChevronRight, ChevronLeft, Tag, MapPin, Loader2
} from 'lucide-react';
import { whatsappAPI } from '../../services/api';

/**
 * Recipient Selection Modal - Step 1 of Broadcast Wizard
 * Allows selecting recipients from Properties and Leads with filtering
 */
const RecipientSelectionModal = ({ isOpen, onClose, onSelect, initialSelected = [] }) => {
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState(initialSelected);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    type: null,
    status: null,
    lifecycleStatus: null,
    city: null,
    labels: []
  });
  const [filterOptions, setFilterOptions] = useState({
    cities: [],
    labels: [],
    statuses: [],
    lifecycleStatuses: [],
    recipientTypes: []
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadFilterOptions();
      loadRecipients();
      setSelected(initialSelected);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      loadRecipients();
    }
  }, [filters, search]);

  const loadFilterOptions = async () => {
    try {
      const response = await whatsappAPI.getRecipientFilters();
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const loadRecipients = async () => {
    try {
      setLoading(true);
      const params = {
        search: search || undefined,
        type: filters.type || undefined,
        status: filters.status || undefined,
        lifecycleStatus: filters.lifecycleStatus || undefined,
        city: filters.city || undefined,
        labels: filters.labels.length > 0 ? filters.labels.join(',') : undefined
      };
      const response = await whatsappAPI.getRecipients(params);
      setRecipients(response.data.data || []);
    } catch (error) {
      console.error('Error loading recipients:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (recipient) => {
    setSelected(prev => {
      const exists = prev.find(r => r.id === recipient.id && r.type === recipient.type);
      if (exists) {
        return prev.filter(r => !(r.id === recipient.id && r.type === recipient.type));
      } else {
        return [...prev, recipient];
      }
    });
  };

  const selectAll = () => {
    // Only select recipients with valid phone numbers
    const validRecipients = recipients.filter(r => r.phone && r.phone.length >= 10);
    setSelected(validRecipients);
  };

  const clearAll = () => {
    setSelected([]);
  };

  const isSelected = (recipient) => {
    return selected.some(r => r.id === recipient.id && r.type === recipient.type);
  };

  const handleConfirm = () => {
    onSelect(selected);
    onClose();
  };

  const resetFilters = () => {
    setFilters({
      type: null,
      status: null,
      lifecycleStatus: null,
      city: null,
      labels: []
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.type) count++;
    if (filters.status) count++;
    if (filters.lifecycleStatus) count++;
    if (filters.city) count++;
    if (filters.labels.length > 0) count++;
    return count;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Select Recipients</h3>
            <p className="text-sm text-gray-500">Choose properties and leads to send message</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="px-6 py-3 border-b border-gray-100 space-y-3">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone, city..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${
                showFilters || getActiveFilterCount() > 0
                  ? 'border-teal-500 bg-teal-50 text-teal-700'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Filter size={18} />
              Filters
              {getActiveFilterCount() > 0 && (
                <span className="bg-teal-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {getActiveFilterCount()}
                </span>
              )}
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg">
              {/* Type Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select
                  value={filters.type || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value || null }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                >
                  <option value="">All Types</option>
                  <option value="property">Properties</option>
                  <option value="lead">Leads</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value || null }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                >
                  <option value="">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              {/* Lifecycle Status Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Lifecycle</label>
                <select
                  value={filters.lifecycleStatus || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, lifecycleStatus: e.target.value || null }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                >
                  <option value="">All Lifecycle</option>
                  <option value="Onboarding">Onboarding</option>
                  <option value="Live">Live</option>
                  <option value="Offboarding">Offboarding</option>
                  <option value="Discontinued">Discontinued</option>
                </select>
              </div>

              {/* City Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                <select
                  value={filters.city || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value || null }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                >
                  <option value="">All Cities</option>
                  {filterOptions.cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              {/* Labels Filter */}
              {filterOptions.labels.length > 0 && (
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Labels</label>
                  <div className="flex flex-wrap gap-2">
                    {filterOptions.labels.map(label => (
                      <button
                        key={label.id}
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            labels: prev.labels.includes(label.id)
                              ? prev.labels.filter(l => l !== label.id)
                              : [...prev.labels, label.id]
                          }));
                        }}
                        className={`px-2 py-1 text-xs rounded-full transition-colors ${
                          filters.labels.includes(label.id)
                            ? 'bg-teal-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                        style={filters.labels.includes(label.id) ? {} : { backgroundColor: label.color + '20', color: label.color }}
                      >
                        {label.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Reset Button */}
              <div className="flex items-end">
                <button
                  onClick={resetFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Selection Actions */}
        <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-4">
            <button
              onClick={selectAll}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              Select All ({recipients.filter(r => r.phone && r.phone.length >= 10).length})
            </button>
            <button
              onClick={clearAll}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear All
            </button>
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium text-teal-600">{selected.length}</span> selected
          </div>
        </div>

        {/* Recipients List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="animate-spin text-teal-600" size={32} />
            </div>
          ) : recipients.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recipients.map(recipient => (
                <div
                  key={`${recipient.type}-${recipient.id}`}
                  onClick={() => toggleSelect(recipient)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    isSelected(recipient)
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`recipient-${recipient.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center mt-0.5 ${
                      isSelected(recipient)
                        ? 'bg-teal-600 border-teal-600'
                        : 'border-gray-300'
                    }`}>
                      {isSelected(recipient) && <Check size={14} className="text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {recipient.type === 'property' ? (
                          <Building2 size={14} className="text-blue-600" />
                        ) : (
                          <Users size={14} className="text-purple-600" />
                        )}
                        <span className="font-medium text-gray-900 truncate">{recipient.name}</span>
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">{recipient.ownerName}</div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        <span>{recipient.phone}</span>
                        {recipient.city && (
                          <span className="flex items-center gap-1">
                            <MapPin size={10} />
                            {recipient.city}
                          </span>
                        )}
                      </div>
                      {recipient.labels && recipient.labels.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {recipient.labels.slice(0, 2).map(label => (
                            <span key={label} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                              {label}
                            </span>
                          ))}
                          {recipient.labels.length > 2 && (
                            <span className="text-xs text-gray-400">+{recipient.labels.length - 2}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <Users size={48} className="mb-2 text-gray-300" />
              <p>No recipients found</p>
              <p className="text-sm">Try adjusting your search or filters</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-white">
          <div className="text-sm text-gray-600">
            {selected.length > 0 ? (
              <span>
                <span className="font-semibold text-teal-600">{selected.length}</span> recipients selected
              </span>
            ) : (
              <span>Select recipients to continue</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selected.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm Selection
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipientSelectionModal;
